import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Typography,
	message,
	notification,
	Modal,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PiEyeDuotone, PiCheckCircleDuotone } from "react-icons/pi";
import AppContext from "../../store/app.context";
import {
	requestGetShopList,
	requestVerifyShop,
} from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title } = Typography;

function ShopsListPage() {
	const context = useContext(AppContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [shops, setShops] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
	});

	useEffect(() => {
		document.title = "Shopiva Console | Shops Management";
		fetchShops();
	}, []);

	useEffect(() => {
		fetchShops();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchShops = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		// Add filter fields here as needed
		// Example: if (filters.status) filterParts.push(`status:${filters.status}`);
		// Example: if (filters.verified) filterParts.push(`verified:${filters.verified}`);

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "_id:desc",
		};

		requestGetShopList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const shopsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setShops(shopsData);
				setPagination({
					...pagination,
					total: serverPagination.total || 0,
				});
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch shops",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSearch = (value) => {
		setFilters({ ...filters, keyword: value });
		setPagination({ ...pagination, current: 1 });
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			...pagination,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	const handleVerifyShop = (shop) => {
		Modal.confirm({
			title: "Verify Shop",
			content: `Are you sure you want to verify ${shop.name}?`,
			okText: "Verify",
			cancelText: "Cancel",
			onOk: () => {
				requestVerifyShop(shop._id)
					.then(() => {
						message.success("Shop verified successfully!");
						fetchShops();
					})
					.catch((error) => {
						notification.error({
							message: "Error",
							description: error.response?.data?.message || "Failed to verify shop",
						});
					});
			},
		});
	};

	const columns = [
		{
			title: "Shop Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
						{record.slug}
					</div>
				</div>
			),
		},
		{
			title: "Contact",
			dataIndex: "email",
			key: "contact",
			width: 200,
			render: (email, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{email}</div>
					<div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{record.phone}</div>
				</div>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			width: 250,
			ellipsis: true,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			align: 'center',
			width: 100,
			render: (status) => (
				<Tag color={status === "VERIFIED" ? "green" : "orange"}>
					{status === "VERIFIED" ? "Verified" : "Unverified"}
				</Tag>
			),
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			align: 'center',
			width: 120,
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Actions",
			key: "actions",
			align: 'right',
			width: 120,
			render: (_, record) => (
				<Space size="small">
					<Button type="text" icon={<PiEyeDuotone />} title="View Details" />
					{record.status !== "VERIFIED" && (
						<Button
							type="text"
							icon={<PiCheckCircleDuotone />}
							title="Verify Shop"
							style={{ color: "#52c41a" }}
							onClick={() => handleVerifyShop(record)}
						/>
					)}
				</Space>
			),
		},
	];

	return (
		<div>
			<div style={{ marginBottom: 24 }}>
				<Title level={3}>Shops Management</Title>
			</div>

			{/* Shops Table */}
			<Card title="Shop List">
				<div style={{ marginBottom: 16 }}>
					<SearchInput
						placeholder="Search shops by name, description, or owner..."
						onSearch={handleSearch}
						defaultValue={filters.keyword}
					/>
				</div>
				<Table
					columns={columns}
					dataSource={shops}
					rowKey="_id"
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} shops`,
					}}
					onChange={handleTableChange}
					scroll={{ x: 1000 }}
				/>
			</Card>
		</div>
	);
}

export default ShopsListPage;
